import React, { useEffect, useRef, useState } from "react";
import { View, TextInput, Pressable, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";
import Text from "@/components/shared/AppText";
import { ErrorText } from "@/components/settings/Field";
import { t } from "@/localization";

const AppInput = React.memo(
  ({
    textarea = false,
    style,
    label,
    labelStyle,
    inputStyle,
    text,
    placeholder,
    secureTextEntry,
    onPress,
    errorMessage,
    onSubmitOnEnter,
    onIconRightAction,
    iconRightName,
    iconRightColor,
    rightComponent,
    onItemPress,
    rowSpan = 4,
    disabled = false,
    ...extraProps
  }) => {
    const [isFocused, setIsFocused] = useState(false);
    // A copy icon shows a green check for a moment after it's pressed.
    const isCopyIcon = iconRightName === "copy-outline";
    const [copied, setCopied] = useState(false);
    const copiedTimer = useRef(null);
    useEffect(() => () => clearTimeout(copiedTimer.current), []);
    const [isPasswordVisible, setIsPasswordVisible] = useState(
      secureTextEntry === true
    );

    if (onSubmitOnEnter && typeof onSubmitOnEnter === "function") {
      extraProps.onKeyPress = (e) => {
        if (e.key === "Enter") {
          onSubmitOnEnter();
        }
      };
    }

    // Prevent keyboard from opening on mobile when disabled
    const shouldPreventInput = disabled && Platform.OS !== "web";

    return (
      <>
        <View
          style={[
            tw`flex-row items-center px-4 py-0 rounded-lg bg-white border border-gray-300 mb-4`,
            isFocused && tw`border-blue-500`,
            // Errors look as in the settings kit: red border, the message
            // close under the field.
            !!errorMessage && tw`border-[#DC2626] mb-0`,
            style,
          ]}
        >
          {label && (
            <Text
              style={[tw`ml-4 flex-grow-0 text-black font-bold`, labelStyle]}
            >
              {label}
            </Text>
          )}
          <View style={tw`flex-1`}>
            <TextInput
              // pointerEvents={onItemPress ? "none" : null}
              style={[
                tw`w-full text-left`,
                !textarea && tw`h-[50px]`,
                textarea && { minHeight: rowSpan * 20 },
                textarea &&
                  Platform.OS === "android" && {
                    textAlignVertical: "top",
                  },
                Platform.OS === "web" && { outlineStyle: "none" },
                inputStyle,
              ]}
              multiline={textarea}
              numberOfLines={textarea ? rowSpan : 1}
              {...extraProps}
              editable={!shouldPreventInput}
              onPress={onItemPress}
              secureTextEntry={isPasswordVisible}
              placeholder={placeholder}
              onBlur={(ev) => {
                setIsFocused(false);
                extraProps.onBlur && extraProps.onBlur(ev);
              }}
              onFocus={(ev) => {
                setIsFocused(true);
                extraProps.onFocus && extraProps.onFocus(ev);
              }}
            />
          </View>

          {/* Password Toggle */}
          {secureTextEntry && (
            <Pressable
              onPress={() => {
                setIsPasswordVisible(!isPasswordVisible);
              }}
              style={tw`p-2`}
            >
              <Ionicons
                name={!isPasswordVisible ? "eye-off-outline" : "eye-outline"}
                size={24}
                color={isFocused ? "#3b82f6" : "#6b7280"}
              />
            </Pressable>
          )}

          {/* Right Icon with Action */}
          {!secureTextEntry && onIconRightAction && (
            <Pressable
              style={({ pressed }) => [
                tw`p-2`,
                { opacity: pressed ? 0.3 : 1.0 },
              ]}
              onPress={(e) => {
                onIconRightAction(e);
                if (isCopyIcon) {
                  setCopied(true);
                  clearTimeout(copiedTimer.current);
                  copiedTimer.current = setTimeout(
                    () => setCopied(false),
                    1800
                  );
                }
              }}
              accessibilityRole="button"
              accessibilityLabel={
                isCopyIcon ? (copied ? t("Copied") : t("Copy")) : undefined
              }
            >
              <Ionicons
                name={copied ? "checkmark" : iconRightName}
                size={24}
                color={copied ? "#15803D" : "#6b7280"}
              />
            </Pressable>
          )}

          {/* Right Icon without Action */}
          {!secureTextEntry && !onIconRightAction && iconRightName && (
            <Ionicons
              name={iconRightName}
              size={24}
              color="#6b7280"
              style={tw`p-2`}
            />
          )}

          {rightComponent && rightComponent}
        </View>
        {!!errorMessage && (
          <ErrorText style={tw`mb-3`}>{errorMessage}</ErrorText>
        )}
      </>
    );
  }
);

export default AppInput;
